/**
 * The self-check pages' reading of the answers, and the line a conversation
 * opens on (2026-10-27). /check/<slug> sends a person to
 * /talk?mode=chat&check=<slug>&a=<one digit per question, 0–2> — which check
 * and what was ticked, never a word they typed. The page's preview of the first
 * line and the chat's actual first line are both written here from those
 * digits, so what the person reads before sending is what is sent.
 *
 * The question wording lives in the page's HTML (pages/check/<slug>.html, read
 * by search engines); this file holds, per question in the same order, the
 * sentence a ticked "most" answer adds to the first line and the chip that
 * echoes it on the result.
 */

export const CHECKS = Object.freeze({
  'one-sided-friendship': Object.freeze({
    opener: 'I think a friendship of mine might be one-sided.',
    openerNone: "I've been wondering whether a friendship of mine is one-sided.",
    questions: Object.freeze([
      Object.freeze({ say: "I'm usually the one who texts or calls first", chip: 'You text first' }),
      Object.freeze({ say: 'plans only happen when I make them', chip: 'Plans start with you' }),
      Object.freeze({ say: 'when I go quiet, it goes quiet too', chip: 'Silence when you stop' }),
      Object.freeze({ say: 'I know much more about their life than they know about mine', chip: 'You know more about them' }),
      Object.freeze({ say: 'they mostly get in touch when they need something', chip: 'They call when they need you' }),
      Object.freeze({ say: "when I needed something, I didn't feel I could ask them", chip: "You can't lean back" }),
      Object.freeze({ say: 'I brought it up once and it got better for a while, then went back', chip: "You've said it before" }),
      Object.freeze({ say: "they're one of the few close people I have", chip: 'Few others close by' }),
    ]),
    // Highest total first; every total 0–16 falls in exactly one.
    bands: Object.freeze([
      Object.freeze({ key: 'high', min: 11, close: "I'm tired of carrying it, and I don't know whether to keep going." }),
      Object.freeze({ key: 'mid', min: 6, close: "Some of it feels uneven and I can't tell what to make of it." }),
      Object.freeze({ key: 'low', min: 0, close: "It's more balanced than it looks on paper, but something about it still bothers me." }),
    ]),
    // Which of the light's findings leads the result: the first question here
    // answered with its "most" option names it; otherwise the last entry's.
    leads: Object.freeze([[6, 'said'], [2, 'stop'], [7, 'held'], [null, 'stop']]),
    // How many ticked sentences the first line carries at most.
    sayMax: 4,
  }),
});

/** The answers as numbers (0–2 each), or null when the slug is unknown or the digits don't fit it. */
export function parseCheckAnswers(slug, digits) {
  const check = Object.prototype.hasOwnProperty.call(CHECKS, String(slug || '')) ? CHECKS[slug] : null;
  const s = String(digits || '');
  if (!check || s.length !== check.questions.length || !/^[0-2]+$/.test(s)) return null;
  return Array.from(s, Number);
}

/** What the result shows for these answers: band key, the "most" answers' chips, and the finding that leads. */
export function readCheck(slug, answers) {
  const check = CHECKS[slug];
  const total = answers.reduce((sum, v) => sum + v, 0);
  const band = check.bands.find((b) => total >= b.min);
  const strong = answers.map((v, i) => (v === 2 ? i : -1)).filter((i) => i >= 0);
  const lead = check.leads.find(([i]) => i === null || answers[i] === 2)[1];
  return { total, band: band.key, chips: strong.map((i) => check.questions[i].chip), lead };
}

/** The conversation's first line, in the person's voice, or '' when the link names no check or answers we can read. */
export function checkTalkLine(slug, digits) {
  const answers = parseCheckAnswers(slug, digits);
  if (!answers) return '';
  const check = CHECKS[slug];
  const band = check.bands.find((b) => answers.reduce((sum, v) => sum + v, 0) >= b.min);
  const says = answers.map((v, i) => (v === 2 ? check.questions[i].say : null)).filter(Boolean).slice(0, check.sayMax);
  if (!says.length) return `${check.openerNone} ${band.close}`;
  const listed = says.length < 2 ? says[0] : `${says.slice(0, -1).join(', ')}, and ${says[says.length - 1]}`;
  return `${check.opener} ${listed.charAt(0).toUpperCase()}${listed.slice(1)}. ${band.close}`;
}
